import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

function unwrap(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
}

export function upsertMyTemplateListCache(qc, item) {
  if (!item?.id) return
  qc.setQueryData(['counsel-surveys', 'my-templates', 'list'], (old) => {
    const list = Array.isArray(old) ? old : []
    return [item, ...list.filter((row) => row?.id !== item.id)]
  })
}
function deepClone(value) {
  return JSON.parse(JSON.stringify(value || {}))
}

const SURVEY_OPTION_TYPES = ['single-select', 'multi-select', 'select']

const SURVEY_QUESTION_TYPE_OPTIONS = [
  { value: 'text', label: '텍스트' },
  { value: 'long-text', label: '긴 텍스트' },
  { value: 'single-select', label: '단일 선택' },
  { value: 'multi-select', label: '복수 선택' },
  { value: 'select', label: '목록 선택' },
]

/** STEP2~5 ↔ 운영 COMMON 템플릿 섹션 키(basic/coverage/needs/closing). 없으면 동일 순서의 섹션에 매핑 */
const WIZARD_BODY_SECTION_KEYS = ['basic', 'coverage', 'needs', 'closing']

const WIZARD_STEPS = [
  { step: 1, title: '설문 제목·설명', subtitle: '설문 제목과 설명' },
  { step: 2, title: '기본정보', subtitle: '기본 고객 정보 문항' },
  { step: 3, title: '보험가입현황', subtitle: '현재 보험·가입 관련 문항' },
  { step: 4, title: '핵심 니즈와 불편경험', subtitle: '니즈·불편 경험 문항' },
  { step: 5, title: '기타(상담메모 등)', subtitle: '상담 메모·추가 메모 문항' },
]

/** 설문 생성(맞춤) 시 빈 STEP 뼈대 — 운영 템플릿과 동일한 섹션 키를 사용 */
export function emptyWizardSchema() {
  return {
    title: '',
    description: '',
    sections: WIZARD_BODY_SECTION_KEYS.map((key, slot) => {
      const meta = WIZARD_STEPS[slot + 1]
      return {
        key,
        title: meta?.title || key,
        description: '',
        questions: [],
      }
    }),
  }
}

function resolveWizardSectionIndex(stepId, schema) {
  if (stepId < 2 || stepId > 5) return null
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  const slot = stepId - 2
  const preferredKey = WIZARD_BODY_SECTION_KEYS[slot]
  const byKey = sections.findIndex((s) => String(s?.key || '') === preferredKey)
  if (byKey >= 0) return byKey
  if (slot < sections.length) return slot
  return null
}

function isSurveyOptionType(type) {
  return SURVEY_OPTION_TYPES.includes(String(type || '').toLowerCase())
}

/** 편집기 드롭다운용: 스키마의 type이 목록에 없으면 가장 가까운 값으로 표시 */
function editorQuestionTypeSelectValue(rawType) {
  const t = String(rawType || 'text').toLowerCase()
  if (SURVEY_QUESTION_TYPE_OPTIONS.some((o) => o.value === t)) return t
  if (isSurveyOptionType(t)) return t
  if (['tel', 'date'].includes(t)) return 'text'
  return 'text'
}

function shouldShowAnswerOptionsEditor(question) {
  const t = String(question?.questionType || '').toLowerCase()
  if (isSurveyOptionType(t)) return true
  return Array.isArray(question?.options) && question.options.length > 0
}

function ensureSectionsOnSchema(prev) {
  const next = deepClone(prev)
  if (!Array.isArray(next.sections) || next.sections.length === 0) {
    next.sections = [{ key: 'main', title: '설문', description: '', questions: [] }]
  }
  next.sections.forEach((sec) => {
    if (!Array.isArray(sec.questions)) sec.questions = []
  })
  return next
}

function ensureSchemaTitle(schema, fallbackName) {
  const next = ensureSectionsOnSchema(schema)
  if (!String(next.title || '').trim() && fallbackName) next.title = String(fallbackName)
  return next
}

function collectAllQuestionKeys(schema) {
  const keys = new Set()
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  sections.forEach((sec) => {
    (Array.isArray(sec?.questions) ? sec.questions : []).forEach((q) => {
      if (q?.key) keys.add(String(q.key))
    })
  })
  return keys
}

function validateSurveySchemaKeys(schema) {
  const seen = new Map()
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  for (let si = 0; si < sections.length; si++) {
    const qs = Array.isArray(sections[si]?.questions) ? sections[si].questions : []
    for (let qi = 0; qi < qs.length; qi++) {
      const k = String(qs[qi]?.key || '').trim()
      if (!k) {
        return `문항 ID(key)가 비어 있습니다. (${sections[si]?.title || `섹션 ${si + 1}`} · ${qi + 1}번째 문항)`
      }
      if (seen.has(k)) {
        return `문항 ID "${k}" 이(가) 중복되었습니다. 각 문항은 서로 다른 ID를 사용해야 합니다.`
      }
      seen.set(k, true)
    }
  }
  return null
}

function validateSurveyOptionQuestions(schema) {
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  for (const sec of sections) {
    const qs = Array.isArray(sec?.questions) ? sec.questions : []
    for (const q of qs) {
      if (!isSurveyOptionType(q?.type)) continue
      const opts = Array.isArray(q.options) ? q.options : []
      if (opts.length === 0) {
        return `선택형 문항에 보기가 1개 이상 필요합니다. (${q.label || q.key || '제목 없음'})`
      }
    }
  }
  return null
}

function flattenBaseQuestionBank(baseSchema) {
  const sections = Array.isArray(baseSchema?.sections) ? baseSchema.sections : []
  return sections.flatMap((section, sectionIndex) =>
    (Array.isArray(section?.questions) ? section.questions : []).map((question) => ({
      sectionKey: section.key != null ? String(section.key) : `s${sectionIndex}`,
      sectionTitle: section?.title || section?.key || `섹션 ${sectionIndex + 1}`,
      question: deepClone(question),
    })))
}

function flattenQuestionBankEntries(templateSchema) {
  const bank = Array.isArray(templateSchema?.questionBank) ? templateSchema.questionBank : []
  return bank
    .filter((e) => e && e.question && e.question.key)
    .map((e) => ({
      sectionKey: e.sectionKey != null ? String(e.sectionKey) : 'extras',
      sectionTitle: e.sectionTitle || '추가 문항',
      question: deepClone(e.question),
    }))
}

function collectEditableQuestions(schema) {
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  return sections.flatMap((section, sectionIndex) =>
    (Array.isArray(section?.questions) ? section.questions : []).map((question, questionIndex) => ({
      sectionIndex,
      questionIndex,
      sectionTitle: section?.title || section?.key || `섹션 ${sectionIndex + 1}`,
      key: question?.key || `question-${questionIndex + 1}`,
      questionType: question?.type || 'text',
      label: question?.label || '',
      options: (Array.isArray(question?.options) ? question.options : []).map((opt, optionIndex) => ({
        optionIndex,
        text: typeof opt === 'string' ? opt : (opt?.label ?? opt?.value ?? ''),
      })),
    })))
}

export function listSurveyTemplateQuestionRows(schema) {
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  return sections.flatMap((section, sectionIndex) => {
    const sectionTitle = section?.title || section?.key || `섹션 ${sectionIndex + 1}`
    const questions = Array.isArray(section?.questions) ? section.questions : []
    return questions.map((question, questionIndex) => {
      const key = String(question?.key || '').trim()
      return {
        key,
        label: question?.label || key || `문항 ${questionIndex + 1}`,
        sectionTitle,
        sectionIndex,
        questionIndex,
      }
    })
  })
}

export function findMissingSurveyQuestionRows(expectedSchema, actualSchema) {
  const expectedRows = listSurveyTemplateQuestionRows(expectedSchema).filter((row) => row.key)
  const actualKeys = new Set(listSurveyTemplateQuestionRows(actualSchema).map((row) => row.key).filter(Boolean))
  return expectedRows.filter((row) => !actualKeys.has(row.key))
}

export function buildPostSaveMissingQuestionMessage(missingRows) {
  const missing = Array.isArray(missingRows) ? missingRows : []
  const preview = missing
    .slice(0, 3)
    .map((row) => row.label || row.key)
    .filter(Boolean)
    .join(', ')
  const suffix = preview ? ` (${preview}${missing.length > 3 ? ' 외' : ''})` : ''
  return `저장 후 다시 확인한 결과 ${missing.length}개 문항이 누락되었습니다${suffix}. 저장 내용을 확인해 주세요.`
}

function findQuestionLocation(schema, keyId) {
  const k = String(keyId || '')
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  for (let si = 0; si < sections.length; si++) {
    const qs = Array.isArray(sections[si]?.questions) ? sections[si].questions : []
    for (let qi = 0; qi < qs.length; qi++) {
      if (String(qs[qi]?.key || '') === k) {
        return { sectionIndex: si, questionIndex: qi }
      }
    }
  }
  return null
}

function entryMatchesSection(entry, section) {
  if (!entry || !section) return false
  if (section.key != null && entry.sectionKey != null && String(entry.sectionKey) === String(section.key)) return true
  if (section.title && entry.sectionTitle && String(entry.sectionTitle) === String(section.title)) return true
  return false
}

/**
 * 카테고리 기본 스키마 → 문항 피커용 목록 (sections 전체를 위에서부터 이어 번호 매김, 이어서 questionBank)
 */
function flattenCatalogQuestionsForPicker(schema, categoryCode, categoryName) {
  const labelCat = categoryName || categoryCode || ''
  const rows = []
  let ordinal = 0
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  sections.forEach((sec, si) => {
    const sectionTitle = sec?.title || sec?.key || `섹션 ${si + 1}`
    const qs = Array.isArray(sec?.questions) ? sec.questions : []
    qs.forEach((q, qi) => {
      if (!q || typeof q !== 'object') return
      ordinal += 1
      rows.push({
        id: `${categoryCode}-s${si}-q${qi}-${String(q.key ?? qi)}`,
        ordinal,
        sectionTitle,
        question: deepClone(q),
        categoryCode,
        categoryLabel: labelCat,
      })
    })
  })
  const bank = Array.isArray(schema?.questionBank) ? schema.questionBank : []
  bank.forEach((entry, bi) => {
    if (!entry?.question || typeof entry.question !== 'object') return
    ordinal += 1
    const st = entry.sectionTitle || entry.sectionKey || '문항 은행'
    rows.push({
      id: `${categoryCode}-bank-${bi}-${String(entry.question.key ?? bi)}`,
      ordinal,
      sectionTitle: `추천 · ${st}`,
      question: deepClone(entry.question),
      categoryCode,
      categoryLabel: labelCat,
    })
  })
  return rows
}

function surveyQuestionTypeLabel(type) {
  const t = String(type || 'text').toLowerCase()
  const hit = SURVEY_QUESTION_TYPE_OPTIONS.find((o) => o.value === t)
  if (hit) return hit.label
  if (t === 'tel') return '전화'
  if (t === 'date') return '날짜'
  if (t === 'number') return '숫자'
  return String(type || '텍스트')
}

function formatPreviewOptionLine(opt) {
  if (opt == null) return ''
  if (typeof opt === 'string') return opt
  const v = opt.value != null ? String(opt.value) : ''
  const l = opt.label != null ? String(opt.label) : ''
  if (v && l && v !== l) return `${l}`
  return l || v || ''
}

function CatalogQuestionPreviewModal({ row, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!row?.question) return null
  const q = row.question
  const t = String(q.type || 'text').toLowerCase()
  const opts = Array.isArray(q.options) ? q.options : []
  const showOpts = isSurveyOptionType(t)
  const label = String(q.label || q.key || '질문').trim() || '질문'

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="catalog-q-preview-title"
      onClick={onClose}
    >
      <div
        className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
          <h2 id="catalog-q-preview-title" className="text-lg font-semibold text-slate-900">문항 보기</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            닫기
          </button>
        </div>

        <div className="mt-4 space-y-4 text-sm">
          <div className="text-xs text-slate-500">
            <span className="font-mono font-semibold text-teal-800">#{row.ordinal}</span>
            <span className="mx-1.5 text-slate-300">·</span>
            <span>{row.sectionTitle}</span>
            <span className="rounded-md border border-slate-200 bg-slate-50 ml-2 px-2 py-1 text-xs font-medium text-slate-700">
              <span className="font-medium text-slate-700">유형: </span> {surveyQuestionTypeLabel(q.type)}
            </span>
            {q.required ? (
              <span className="rounded-md border border-rose-200 bg-rose-50 ml-2 px-2 py-1 font-medium text-rose-800">필수</span>
            ) : (
              <span className="rounded-md border border-slate-200 bg-white ml-2 px-2 py-1 text-slate-600">선택</span>
            )}
          </div>
          <div className="text-base font-semibold text-slate-900">{label}</div>
          {q.placeholder ? (
            <div className="rounded-md border border-slate-100 bg-slate-50/80 px-3 py-2 text-xs text-slate-600">
              <span className="font-medium text-slate-700">입력 안내: </span>
              {String(q.placeholder)}
            </div>
          ) : null}

          {q.helpText ? (
            <div className="text-xs leading-relaxed text-slate-600">{String(q.helpText)}</div>
          ) : null}

          {showOpts && (
            <div>
              <div className="text-xs font-semibold text-slate-700">보기</div>
              {opts.length === 0 ? (
                <div className="mt-2 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-900">
                  등록된 보기가 없습니다.
                </div>
              ) : (
                <ul className="mt-2 space-y-2">
                  {opts.map((opt, i) => {
                    const line = formatPreviewOptionLine(opt)
                    return (
                      <li
                        key={`pv-opt-${i}-${line.slice(0, 24)}`}
                        className="rounded-lg border border-slate-100 bg-slate-50/90 px-3 py-2.5 text-slate-800"
                      >
                        {t === 'single-select' && (
                          <span className="mr-2 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 border-slate-300 bg-white align-middle" aria-hidden />
                        )}
                        {t === 'multi-select' && (
                          <span className="mr-2 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 border-slate-300 bg-white align-middle" aria-hidden />
                        )}
                        <span className="align-middle">{line || `(보기 ${i + 1})`}</span>
                      </li>
                    )
                  })}
                </ul>
              )}
              {t === 'select' && opts.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-medium text-slate-500">목록 선택 예시</div>
                  <select className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700" disabled defaultValue="">
                    <option value="" disabled>선택…</option>
                    {opts.map((opt, i) => (
                      <option key={`pv-sel-${i}`} value={String(typeof opt === 'string' ? opt : (opt?.value ?? opt?.label ?? i))}>
                        {formatPreviewOptionLine(opt) || `항목 ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {!showOpts && t === 'long-text' && (
            <div>
              <div className="text-xs font-medium text-slate-500">응답 영역 (예시)</div>
              <textarea className="mt-1 min-h-[5rem] w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-400" disabled readOnly value="" placeholder="여러 줄 텍스트" />
            </div>
          )}

          {!showOpts && t !== 'long-text' && (
            <div>
              <div className="text-xs font-medium text-slate-500">응답 영역 (예시)</div>
              <input
                type={t === 'date' ? 'date' : t === 'tel' ? 'tel' : t === 'number' ? 'number' : 'text'}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-400"
                disabled
                readOnly
                value=""
                placeholder={t === 'date' ? '' : '한 줄 입력'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TemplateQuestionInsertDivider({ insertIndex, onInsert }) {
  return (
    <div className="group relative flex h-12 items-center">
      <span
        className="h-px w-full bg-slate-300 transition-colors group-hover:bg-brand-sky/80 group-focus-within:bg-brand-sky/80"
        aria-hidden="true"
      />
      <button
        type="button"
        className="absolute right-2 top-1/2 z-10 inline-flex min-h-10 -translate-y-1/2 items-center rounded-full border border-brand-sky/60 bg-white px-3 py-1.5 text-xs font-semibold text-brand-navy opacity-75 shadow-sm transition hover:border-brand-sky hover:bg-sky-50 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky/40 group-focus-within:opacity-100"
        aria-label={`질문 ${insertIndex} 다음에 질문 추가`}
        onClick={() => onInsert(insertIndex)}
      >
        + 질문 추가
      </button>
    </div>
  )
}

function SurveyQuestionEditorCard({
  question,
  setCardRef,
  updateQuestionLabel,
  updateQuestionType,
  updateQuestionOptionText,
  addOptionRow,
  removeOptionRow,
  removeQuestion,
  hideSectionBadge,
}) {
  const id = String(question.key)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const combinedRef = (node) => {
    setNodeRef(node)
    if (typeof setCardRef === 'function') setCardRef(node)
  }

  const showOptionsBlock = shouldShowAnswerOptionsEditor(question)
  const typeSelectValue = editorQuestionTypeSelectValue(question.questionType)

  return (
    <div
      ref={combinedRef}
      style={style}
      className={`rounded-lg border bg-white p-2.5 shadow-sm ${isDragging ? 'opacity-55 ring-2 ring-brand-sky/35' : 'border-slate-200'}`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          className="shrink-0 touch-none rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
          aria-label="순서 변경"
        >
          <GripVertical className="h-4 w-4" strokeWidth={2} />
        </button>

        <div className="min-w-0 flex-1">
          {!hideSectionBadge && (
            <div className="mb-0.5 text-[10px] font-semibold text-slate-500">{question.sectionTitle}</div>
          )}
          <label className="sr-only">질문</label>
          <input
            className="w-full rounded-md border border-slate-200/80 bg-slate-50/80 px-2 py-1.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-brand-sky focus:bg-white focus:outline-none"
            placeholder="질문을 입력하세요"
            value={question.label}
            onChange={(e) => updateQuestionLabel(question.sectionIndex, question.questionIndex, e.target.value)}
          />
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <select
            className="max-w-[7.5rem] rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-800"
            value={typeSelectValue}
            onChange={(e) => updateQuestionType(question.sectionIndex, question.questionIndex, e.target.value)}
          >
            {SURVEY_QUESTION_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="rounded-md border border-red-100 bg-white px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
            onClick={() => removeQuestion(question.sectionIndex, question.questionIndex)}
          >
            삭제
          </button>
        </div>
      </div>

      {showOptionsBlock && (
        <div className="mt-2.5 border-t border-slate-100 pt-2.5 pl-7">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-slate-500">보기 (선택지)</span>
            <button
              type="button"
              className="rounded-md border border-dashed border-teal-200 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-100"
              onClick={() => addOptionRow(question.sectionIndex, question.questionIndex)}
            >
              + 보기 추가
            </button>
          </div>
          <div className="space-y-2">
            {(!question.options || question.options.length === 0) && (
              <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-900">
                선택지를 추가해 주세요.
              </div>
            )}
            {(question.options || []).map((opt) => (
              <div
                key={`${question.sectionIndex}-${question.questionIndex}-opt-${opt.optionIndex}`}
                className="flex items-center gap-2"
              >
                <span className="shrink-0 text-[11px] tabular-nums text-slate-400">{opt.optionIndex + 1}.</span>
                <input
                  className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                  value={opt.text}
                  placeholder="보기 텍스트"
                  onChange={(e) => updateQuestionOptionText(
                    question.sectionIndex,
                    question.questionIndex,
                    opt.optionIndex,
                    e.target.value,
                  )}
                />
                <button
                  type="button"
                  className="shrink-0 rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                  disabled={(question.options?.length || 0) <= 1}
                  onClick={() => removeOptionRow(question.sectionIndex, question.questionIndex, opt.optionIndex)}
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function SurveyTemplateEditor({
  item,
  baseSchema,
  createMode = false,
  catalogImportSources = [],
  onCancel,
  onSave,
  saving,
}) {
  const initialSchema = useMemo(() => {
    if (createMode) return ensureSectionsOnSchema(emptyWizardSchema())
    const fallbackName = item?.myTemplate?.name || item?.template?.name || ''
    return ensureSchemaTitle(item?.myTemplate?.schema || item?.template?.schema || {}, fallbackName)
  }, [createMode, item])
  const [schema, setSchema] = useState(() => initialSchema)
  const schemaRef = useRef(schema)
  const fallbackNameRef = useRef(createMode ? '내 맞춤 설문' : (item?.myTemplate?.name || item?.template?.name || ''))
  const setSchemaDraft = (updater) => {
    const next = typeof updater === 'function' ? updater(schemaRef.current) : updater
    schemaRef.current = next
    setSchema(next)
    return next
  }
  const [currentStep, setCurrentStep] = useState(1)
  const [scrollToKey, setScrollToKey] = useState(null)
  const [importPickCode, setImportPickCode] = useState('')
  const [pickerPreviewRow, setPickerPreviewRow] = useState(null)
  const questionRefsMap = useRef({})

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  useEffect(() => {
    if (createMode) return
    if (!item) return
    const nextName = item?.myTemplate?.name || item?.template?.name || ''
    fallbackNameRef.current = nextName || fallbackNameRef.current
    const next = ensureSchemaTitle(item?.myTemplate?.schema || item?.template?.schema || {}, nextName)
    schemaRef.current = next
    setSchema(next)
    setCurrentStep(1)
    setImportPickCode('')
    setPickerPreviewRow(null)
  }, [createMode, item])

  useEffect(() => {
    setPickerPreviewRow(null)
  }, [importPickCode])

  const activeSectionIndex = useMemo(() => resolveWizardSectionIndex(currentStep, schema), [currentStep, schema])
  const activeSection = Array.isArray(schema?.sections) && activeSectionIndex != null
    ? schema.sections[activeSectionIndex]
    : null

  const editableQuestions = useMemo(() => collectEditableQuestions(schema), [schema])
  const sectionMetaList = useMemo(() => {
    if (currentStep < 2 || activeSectionIndex == null) return []
    return editableQuestions.filter((q) => q.sectionIndex === activeSectionIndex)
  }, [editableQuestions, currentStep, activeSectionIndex])

  const sortableIds = useMemo(() => sectionMetaList.map((q) => String(q.key)).filter(Boolean), [sectionMetaList])

  useEffect(() => {
    if (!scrollToKey) return
    const timer = setTimeout(() => {
      const el = questionRefsMap.current[scrollToKey]
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setScrollToKey(null)
    }, 80)
    return () => clearTimeout(timer)
  }, [scrollToKey])

  const currentKeys = useMemo(() => collectAllQuestionKeys(schema), [schema])
  const baseBank = useMemo(() => flattenBaseQuestionBank(baseSchema), [baseSchema])
  const questionBankEntries = useMemo(() => flattenQuestionBankEntries(baseSchema), [baseSchema])
  const restorableFromBase = useMemo(
    () => baseBank.filter((entry) => entry.question?.key && !currentKeys.has(String(entry.question.key))),
    [baseBank, currentKeys],
  )
  const addableFromQuestionBank = useMemo(
    () => questionBankEntries.filter((entry) => entry.question?.key && !currentKeys.has(String(entry.question.key))),
    [questionBankEntries, currentKeys],
  )

  const restorableForStep = useMemo(() => {
    if (!activeSection) return []
    return restorableFromBase.filter((e) => entryMatchesSection(e, activeSection))
  }, [restorableFromBase, activeSection])

  const bankForStep = useMemo(() => {
    if (!activeSection) return []
    return addableFromQuestionBank.filter((e) => entryMatchesSection(e, activeSection))
  }, [addableFromQuestionBank, activeSection])

  const catalogQuestionsForPicker = useMemo(() => {
    if (!importPickCode) return []
    const src = catalogImportSources.find((x) => String(x.code) === String(importPickCode))
    if (!src?.schema) return []
    return flattenCatalogQuestionsForPicker(src.schema, src.code, src.categoryName)
  }, [importPickCode, catalogImportSources])

  const stepMeta = WIZARD_STEPS[currentStep - 1]

  const updateTitle = (value) => setSchemaDraft(prev => ({ ...prev, title: value }))
  const updateDescription = (value) => setSchemaDraft(prev => ({ ...prev, description: value }))

  const updateQuestionLabel = (sectionIndex, questionIndex, value) => {
    setSchemaDraft(prev => {
      const next = deepClone(prev)
      if (next.sections?.[sectionIndex]?.questions?.[questionIndex]) {
        next.sections[sectionIndex].questions[questionIndex].label = value
      }
      return next
    })
  }

  const updateQuestionType = (sectionIndex, questionIndex, type) => {
    setSchemaDraft(prev => {
      const next = deepClone(prev)
      const q = next.sections?.[sectionIndex]?.questions?.[questionIndex]
      if (!q) return prev
      q.type = type
      const t = String(type || '').toLowerCase()
      if (isSurveyOptionType(type)) {
        if (!Array.isArray(q.options) || q.options.length === 0) {
          q.options = ['보기 1', '보기 2']
        }
      } else if (t === 'long-text') {
        delete q.options
      } else {
        delete q.options
      }
      return next
    })
  }

  const updateQuestionOptionText = (sectionIndex, questionIndex, optionIndex, value) => {
    setSchemaDraft(prev => {
      const next = deepClone(prev)
      const q = next.sections?.[sectionIndex]?.questions?.[questionIndex]
      if (!q || !Array.isArray(q.options)) return prev
      if (optionIndex < 0 || optionIndex >= q.options.length) return prev
      const cur = q.options[optionIndex]
      if (typeof cur === 'string') {
        q.options[optionIndex] = value
      } else if (cur && typeof cur === 'object') {
        q.options[optionIndex] = { ...cur, label: value }
      }
      return next
    })
  }

  const addOptionRow = (sectionIndex, questionIndex) => {
    setSchemaDraft(prev => {
      const next = deepClone(prev)
      const q = next.sections?.[sectionIndex]?.questions?.[questionIndex]
      if (!q) return prev
      if (!Array.isArray(q.options)) q.options = []
      q.options.push(`보기 ${q.options.length + 1}`)
      return next
    })
  }

  const removeOptionRow = (sectionIndex, questionIndex, optionIndex) => {
    setSchemaDraft(prev => {
      const next = deepClone(prev)
      const q = next.sections?.[sectionIndex]?.questions?.[questionIndex]
      if (!q || !Array.isArray(q.options)) return prev
      if (q.options.length <= 1) return prev
      q.options.splice(optionIndex, 1)
      return next
    })
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = findQuestionLocation(schemaRef.current, active.id)
    const to = findQuestionLocation(schemaRef.current, over.id)
    if (!from || !to || from.sectionIndex !== to.sectionIndex) return
    setSchemaDraft((prev) => {
      const next = deepClone(prev)
      const qs = next.sections[from.sectionIndex]?.questions
      if (!Array.isArray(qs)) return prev
      next.sections[from.sectionIndex].questions = arrayMove(qs, from.questionIndex, to.questionIndex)
      return next
    })
  }

  const removeQuestion = (sectionIndex, questionIndex) => {
    if (!window.confirm('이 문항을 설문에서 제거할까요?')) return
    setSchemaDraft(prev => {
      const next = deepClone(prev)
      const qs = next.sections?.[sectionIndex]?.questions
      if (!qs) return prev
      qs.splice(questionIndex, 1)
      return next
    })
  }

  const addCustomQuestion = (insertIndex) => {
    if (currentStep < 2 || currentStep > 5) return
    const newKey = `custom_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
    setSchemaDraft(prev => {
      const next = ensureSectionsOnSchema(prev)
      const si = resolveWizardSectionIndex(currentStep, next)
      if (si == null) return prev
      const keys = collectAllQuestionKeys(next)
      let k = newKey
      while (keys.has(k)) {
        k = `custom_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
      }
      const questions = next.sections[si].questions
      const targetIndex = Number.isInteger(insertIndex)
        ? Math.min(Math.max(insertIndex, 0), questions.length)
        : questions.length
      questions.splice(targetIndex, 0, {
        key: k,
        type: 'text',
        label: '새 질문',
        required: false,
      })
      requestAnimationFrame(() => setScrollToKey(k))
      return next
    })
  }

  const restoreQuestionForStep = (entry) => {
    setSchemaDraft(prev => {
      const next = ensureSectionsOnSchema(prev)
      const si = resolveWizardSectionIndex(currentStep, next)
      if (si == null) return prev
      const qClone = deepClone(entry.question)
      next.sections[si].questions = next.sections[si].questions || []
      next.sections[si].questions.push(qClone)
      if (qClone.key) {
        requestAnimationFrame(() => setScrollToKey(qClone.key))
      }
      return next
    })
  }

  const appendCatalogQuestionToCurrentStep = (rawQuestion) => {
    if (currentStep < 2 || currentStep > 5) return
    if (!rawQuestion || typeof rawQuestion !== 'object') return
    let scrollKey = ''
    setSchemaDraft((prev) => {
      const next = ensureSectionsOnSchema(deepClone(prev))
      const si = resolveWizardSectionIndex(currentStep, next)
      if (si == null) return prev
      const keys = collectAllQuestionKeys(next)
      const qc = deepClone(rawQuestion)
      let nk = String(qc.key || '').trim()
      if (!nk) nk = `imp_${Date.now().toString(36)}`
      while (keys.has(nk)) nk = `${nk}_${Math.random().toString(36).slice(2, 5)}`
      qc.key = nk
      next.sections[si].questions = next.sections[si].questions || []
      next.sections[si].questions.push(qc)
      scrollKey = nk
      return next
    })
    if (scrollKey) requestAnimationFrame(() => setScrollToKey(scrollKey))
  }

  const handleImportCategorySection = () => {
    if (!importPickCode) {
      try { window?.toast?.show?.('불러올 카테고리를 선택해 주세요.', 'error') } catch {}
      return
    }
    const src = catalogImportSources.find((x) => String(x.code) === String(importPickCode))
    if (!src?.schema) {
      try { window?.toast?.show?.('선택한 카테고리에 기본 설문이 없습니다.', 'error') } catch {}
      return
    }
    const previewSourceSections = Array.isArray(src.schema.sections) ? src.schema.sections : []
    let previewSourceSection = previewSourceSections.find((s) => String(s?.key || '') === String(activeSection?.key || ''))
    if (!previewSourceSection) {
      const previewSectionIndex = resolveWizardSectionIndex(currentStep, src.schema)
      if (previewSectionIndex != null) previewSourceSection = previewSourceSections[previewSectionIndex]
    }
    const currentCount = Array.isArray(activeSection?.questions) ? activeSection.questions.length : 0
    const nextCount = Array.isArray(previewSourceSection?.questions) ? previewSourceSection.questions.length : 0
    if (!window.confirm(`현재 구역의 ${currentCount}개 문항이 선택 카테고리의 ${nextCount}개 문항으로 대체됩니다. 계속할까요?`)) return
    let failed = false
    setSchemaDraft((prev) => {
      const next = ensureSectionsOnSchema(deepClone(prev))
      const si = resolveWizardSectionIndex(currentStep, next)
      if (si == null) {
        failed = true
        return prev
      }
      const tgtKey = next.sections[si]?.key
      const sourceSections = Array.isArray(src.schema.sections) ? src.schema.sections : []
      let srcSec = sourceSections.find((s) => String(s?.key || '') === String(tgtKey || ''))
      if (!srcSec) {
        const ssi = resolveWizardSectionIndex(currentStep, src.schema)
        if (ssi != null) srcSec = sourceSections[ssi]
      }
      if (!srcSec || !Array.isArray(srcSec.questions)) {
        failed = true
        return prev
      }
      const keys = collectAllQuestionKeys(next)
      const remapped = srcSec.questions.map((q) => {
        const qc = deepClone(q)
        let nk = String(qc.key || '').trim()
        if (!nk) nk = `q_${Date.now().toString(36)}`
        while (keys.has(nk)) nk = `${nk}_${Math.random().toString(36).slice(2, 5)}`
        keys.add(nk)
        qc.key = nk
        return qc
      })
      next.sections[si] = {
        ...next.sections[si],
        title: srcSec.title || next.sections[si].title,
        description: srcSec.description != null ? srcSec.description : next.sections[si].description,
        questions: remapped,
      }
      return next
    })
    if (failed) {
      try { window?.toast?.show?.('해당 구역에 맞는 문항을 찾지 못했습니다.', 'error') } catch {}
    } else {
      try { window?.toast?.show?.('카테고리 기본 문항을 반영했습니다.', 'success') } catch {}
    }
  }

  const handleSaveClick = () => {
    const latestSchema = schemaRef.current
    const beforeRows = listSurveyTemplateQuestionRows(latestSchema)
    const normalized = ensureSectionsOnSchema(latestSchema)
    if (baseSchema && Array.isArray(baseSchema.questionBank) && baseSchema.questionBank.length > 0) {
      normalized.questionBank = deepClone(baseSchema.questionBank)
    }
    const normalizedRows = listSurveyTemplateQuestionRows(normalized)
    const missingRows = findMissingSurveyQuestionRows(latestSchema, normalized)
    if (beforeRows.length !== normalizedRows.length || missingRows.length > 0) {
      const message = missingRows.length > 0
        ? `저장 대상에서 ${missingRows.length}개 문항이 빠졌습니다. 문항 구성을 다시 확인해 주세요.`
        : `화면의 문항 수(${beforeRows.length}개)와 저장 대상 문항 수(${normalizedRows.length}개)가 다릅니다. 문항 구성을 다시 확인해 주세요.`
      try { window?.toast?.show?.(message, 'error') } catch { window.alert(message) }
      return
    }
    const errKey = validateSurveySchemaKeys(normalized)
    if (errKey) {
      try { window?.toast?.show?.(errKey, 'error') } catch { window.alert(errKey) }
      return
    }
    const errOpt = validateSurveyOptionQuestions(normalized)
    if (errOpt) {
      try { window?.toast?.show?.(errOpt, 'error') } catch { window.alert(errOpt) }
      return
    }
    onSave({ name: String(normalized?.title || '').trim() || fallbackNameRef.current || '내 맞춤 설문', schema: normalized, questionKeys: normalizedRows.map((row) => row.key).filter(Boolean) })
  }

  const sectionsLen = Array.isArray(schema?.sections) ? schema.sections.length : 0

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="grid grid-cols-2 gap-2 border-b border-slate-100 bg-slate-50/40 px-3 py-4 sm:grid-cols-5 md:px-6">
        {WIZARD_STEPS.map((ws) => (
          <button
            key={ws.step}
            type="button"
            onClick={() => setCurrentStep(ws.step)}
            className={`rounded-xl border px-2 py-2.5 text-left text-xs transition-colors sm:text-sm ${
              currentStep === ws.step
                ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <span className="block font-mono text-[10px] uppercase tracking-wide opacity-80">STEP {ws.step}</span>
            <span className="mt-0.5 block font-semibold leading-snug">{ws.title}</span>
          </button>
        ))}
      </div>

      {currentStep === 1 && (
        <div className="border-b border-slate-100 bg-white px-4 py-6 md:px-6">
          {/* <p className="mb-4 text-sm text-slate-600">
            {stepMeta?.subtitle}
          </p> */}
          <div className="mx-auto max-w-xl space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">설문 제목</label>
              <input
                className="mt-1 w-full border-0 border-b border-slate-200 bg-transparent px-0 py-1 text-2xl font-semibold text-slate-900 placeholder:text-slate-300 focus:border-brand-sky focus:outline-none"
                placeholder="제목 없는 설문"
                value={schema?.title || ''}
                onChange={(e) => updateTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">설명</label>
              <textarea
                className="mt-1 min-h-[72px] w-full resize-y rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-sky focus:bg-white focus:outline-none"
                placeholder="설문 안내 문구를 적을 수 있습니다."
                value={schema?.description || ''}
                onChange={(e) => updateDescription(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {currentStep >= 2 && (
        <>
          {catalogImportSources.length > 0 && (
            <div className="border-b border-teal-100 bg-teal-50/30 px-4 py-4 md:px-6">
              <div className="text-sm font-semibold text-slate-800">카테고리 기본 설문에서 문항 추가</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label className="text-xs font-medium text-slate-600">카테고리</label>
                <select
                  className="min-w-[12rem] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={importPickCode}
                  onChange={(e) => setImportPickCode(e.target.value)}
                >
                  <option value="">선택…</option>
                  {catalogImportSources.map((row) => (
                    <option key={row.code} value={row.code}>{row.categoryName || row.code} </option>
                  ))}
                </select>
              </div>
              {importPickCode && (
                <div className="mt-3">
                  {catalogQuestionsForPicker.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-white/60 px-3 py-4 text-center text-sm text-slate-500">
                      이 카테고리에 불러올 문항이 없습니다.
                    </div>
                  ) : (
                    <ul className="max-h-[min(22rem,50vh)] space-y-1.5 overflow-y-auto rounded-lg border border-teal-100/80 bg-white/80 p-2">
                      {catalogQuestionsForPicker.map((row) => {
                        const lab = String(row.question?.label || row.question?.key || '제목 없음').trim()
                        const short = lab.length > 72 ? `${lab.slice(0, 72)}…` : lab
                        return (
                          <li
                            key={row.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50/50 px-2 py-2 text-sm"
                          >
                            <div className="min-w-0 flex-1">
                              <span className="font-mono text-xs font-semibold text-teal-800">#{row.ordinal}</span>
                              <span className="mx-1.5 text-slate-300">·</span>
                              <span className="text-xs text-slate-500">{row.sectionTitle}</span>
                              <div className="mt-0.5 text-slate-800">{short}</div>
                            </div>
                            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setPickerPreviewRow(row)}
                                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                              >
                                문항보기
                              </button>
                              <button
                                type="button"
                                onClick={() => appendCatalogQuestionToCurrentStep(row.question)}
                                className="rounded-lg border border-teal-600 bg-teal-50 px-2.5 py-1.5 text-xs font-semibold text-teal-900 hover:bg-teal-100"
                              >
                                이 STEP에 추가
                              </button>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              )}
              <details className="mt-4 rounded-lg border border-slate-200 bg-white/60 px-3 py-2 text-sm">
                <summary className="cursor-pointer font-medium text-slate-700">선택 카테고리로 이 구역 전체 적용 하기</summary>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={!importPickCode}
                    onClick={handleImportCategorySection}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-950 hover:bg-amber-100 disabled:opacity-50"
                  >
                    구역 전체 적용하기
                  </button>
                </div>
              </details>
            </div>
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className="px-4 py-6 md:px-6">
              <div className="mx-auto max-w-xl space-y-8">
                {activeSectionIndex == null ? (
                  <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/50 px-6 py-10 text-center text-sm text-slate-700">
                    카테고리별로 설문 구성이 달라 질문 추가가 불가능합니다.
                  </div>
                ) : (
                  <>
                    {(restorableForStep.length > 0 || bankForStep.length > 0) && (
                      <details className="rounded-xl border border-slate-200 bg-slate-50/60 open:bg-slate-50">
                        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-700">
                          추가 문항 불러오기 (운영 템플릿 · 이 구역만)
                        </summary>
                        <div className="space-y-4 border-t border-slate-200 px-4 pb-4 pt-3">
                          {restorableForStep.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-slate-700">기본 본문에서 빠진 문항</div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {restorableForStep.map((entry, i) => (
                                  <button
                                    key={`${entry.question.key}-restore-${i}`}
                                    type="button"
                                    className="rounded-full border border-sky-200 bg-white px-3 py-1.5 text-left text-xs hover:bg-sky-50"
                                    onClick={() => restoreQuestionForStep(entry)}
                                  >
                                    + {(entry.question.label || entry.question.key || '').slice(0, 44)}
                                    {(entry.question.label || '').length > 44 ? '…' : ''}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {bankForStep.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-slate-700">문항 은행</div>
                              <div className="mt-1 text-xs text-slate-500">추가한 문항만 실제 설문에 포함됩니다.</div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {bankForStep.map((entry, i) => (
                                  <button
                                    key={`${entry.question.key}-bank-${i}`}
                                    type="button"
                                    className="rounded-full border border-violet-200 bg-white px-3 py-1.5 text-left text-xs hover:bg-violet-50"
                                    onClick={() => restoreQuestionForStep(entry)}
                                  >
                                    + {(entry.question.label || entry.question.key || '').slice(0, 44)}
                                    {(entry.question.label || '').length > 44 ? '…' : ''}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </details>
                    )}

                    {sectionMetaList.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-12 text-center text-sm text-slate-500">
                        이 구역에 아직 문항이 없습니다. 아래 「질문 추가」를 누르거나, 문항을 불러오세요.
                      </div>
                    )}

                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                      <div>
                        {sectionMetaList.map((question) => (
                          <React.Fragment key={String(question.key)}>
                            <SurveyQuestionEditorCard
                              question={question}
                              hideSectionBadge
                              setCardRef={(el) => {
                                questionRefsMap.current[question.key] = el
                              }}
                              updateQuestionLabel={updateQuestionLabel}
                              updateQuestionType={updateQuestionType}
                              updateQuestionOptionText={updateQuestionOptionText}
                              addOptionRow={addOptionRow}
                              removeOptionRow={removeOptionRow}
                              removeQuestion={removeQuestion}
                            />
                            {question.questionIndex < sectionMetaList.length - 1 && (
                              <TemplateQuestionInsertDivider insertIndex={question.questionIndex + 1} onInsert={addCustomQuestion} />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </SortableContext>

                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-5">
                      <button
                        type="button"
                        className="w-full rounded-xl bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-800 sm:w-auto"
                        onClick={() => addCustomQuestion()}
                      >
                        + 이 구역에 질문 추가
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </DndContext>
        </>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-4 md:px-6">
        <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
          취소
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={currentStep <= 1}
            onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            이전
          </button>
          {currentStep < 5 && (
            <button
              type="button"
              onClick={() => setCurrentStep((s) => Math.min(5, s + 1))}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              다음
            </button>
          )}
          <button
            type="button"
            onClick={handleSaveClick}
            disabled={saving}
            className="rounded-lg bg-brand-navy px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      {pickerPreviewRow ? (
        <CatalogQuestionPreviewModal row={pickerPreviewRow} onClose={() => setPickerPreviewRow(null)} />
      ) : null}
    </div>
  )
}

export default function SurveyCatalogTemplateEditPage() {
  const { categoryCode: rawCategory } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const categoryCode = rawCategory ? decodeURIComponent(rawCategory) : ''

  const catalogQuery = useQuery({
    queryKey: ['counsel-surveys', 'catalog'],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/catalog', { __silent: true })),
  })

  const catalogItem = useMemo(() => {
    const catalog = Array.isArray(catalogQuery.data) ? catalogQuery.data : []
    return catalog.find((x) => String(x?.category?.code || '') === categoryCode) || null
  }, [catalogQuery.data, categoryCode])

  const catalogImportSources = useMemo(() => {
    const catalog = Array.isArray(catalogQuery.data) ? catalogQuery.data : []
    return catalog
      .filter((x) => x?.ready && x?.template?.schema && String(x?.category?.code || '') !== String(categoryCode || ''))
      .map((x) => ({
        code: x.category.code,
        categoryName: x.category.name,
        schema: x.template.schema,
      }))
  }, [catalogQuery.data, categoryCode])

  const saveMutation = useMutation({
    mutationFn: async ({ item, payload }) => {
      let myTemplateId = item?.myTemplate?.id
      let reloadedId = myTemplateId
      if (!myTemplateId) {
        const created = unwrap(await api.post(`/counsel-surveys/my-templates/from-template/${item.template.id}`, {
          name: payload.name,
          schema: payload.schema,
          publish: true,
          changeSummary: '템플릿 편집 페이지에서 수정',
        }))
        myTemplateId = created.id
        reloadedId = created?.id || myTemplateId
      } else {
        const saved = unwrap(await api.patch('/counsel-surveys/my-templates/' + myTemplateId, {
          name: payload.name,
          schema: payload.schema,
          publish: true,
          changeSummary: '템플릿 편집 페이지에서 수정',
        }))
        reloadedId = saved?.id || myTemplateId
      }
      const reloaded = unwrap(await api.get('/counsel-surveys/my-templates/' + reloadedId, { __silent: true }))
      const missingRows = findMissingSurveyQuestionRows(payload.schema, reloaded?.schema)
      if (missingRows.length > 0) {
        throw new Error(buildPostSaveMissingQuestionMessage(missingRows))
      }
      return reloaded
    },
    onSuccess: async (savedTemplate) => {
      upsertMyTemplateListCache(qc, savedTemplate)
      try { window?.toast?.show?.('설문 구성을 저장했습니다.', 'success') } catch {}
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'catalog'] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'my-templates', 'list'] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'sessions', 'hub'] }),
      ])
      navigate('/support/surveys?tab=list')
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '수정 저장 실패', 'error') } catch {}
    },
  })

  if (catalogQuery.isLoading) return <Loading />
  if (catalogQuery.isError) return <InlineError message="설문 목록을 불러오지 못했습니다." />
  if (!categoryCode) {
    return <InlineError message="카테고리 정보가 없습니다." />
  }
  if (!catalogItem) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center">
        <p className="text-slate-600">해당 카테고리 설문을 찾을 수 없습니다.</p>
        <Link to="/support/surveys?tab=list" className="mt-4 inline-block rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
          설문관리로
        </Link>
      </div>
    )
  }

  if (!catalogItem.ready) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center">
        <p className="text-slate-600">이 카테고리 설문은 아직 사용할 수 없습니다. 운영 템플릿 배포 후 다시 시도해 주세요.</p>
        <Link to="/support/surveys?tab=list" className="mt-4 inline-block rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
          설문관리로
        </Link>
      </div>
    )
  }

  const baseSchema = catalogItem?.template?.schema

  return (
    <div className="pb-10">
      <div className="mb-6">
        <div className="text-sm text-slate-500">
          <Link to="/support/surveys?tab=list" className="hover:text-brand-sky">설문관리</Link>
          <span className="mx-2 text-slate-300">/</span>
          <span className="text-slate-700">템플릿 편집</span>
        </div>
        <h1 className="mt-3 text-2xl font-bold text-slate-900">설문 템플릿 편집</h1>
        <p className="mt-1 text-sm text-slate-500">
          {catalogItem?.category?.name || categoryCode}
          {' · '}
          {catalogItem?.myTemplate?.name || catalogItem?.template?.name || '설문'}
        </p>
      </div>

      <SurveyTemplateEditor
        item={catalogItem}
        baseSchema={baseSchema}
        catalogImportSources={catalogImportSources}
        onCancel={() => navigate('/support/surveys?tab=list')}
        onSave={(payload) => saveMutation.mutate({ item: catalogItem, payload })}
        saving={saveMutation.isPending}
      />
    </div>
  )
}
